import { UIPlugin, DefaultPluginOptions } from '@uppy/core';
import type { Uppy, UppyFile, PluginTarget } from '@uppy/core';

interface SearchPluginOptions extends DefaultPluginOptions {
  target?: PluginTarget;
}

export default class FileSearchPlugin extends UIPlugin {
  private originalFiles: UppyFile[];
  private container: HTMLElement | null;
  private onModalClosed: () => void;
  private onFileAdded: (file: UppyFile) => void;
  private onFileRemoved: (file: UppyFile) => void;
  // Performance and race condition prevention
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;
  private mountingTimeouts: ReturnType<typeof setTimeout>[] = [];
  private isMounting = false;
  private mutationObserver: MutationObserver | null = null;
  private currentSearchQuery = '';

  constructor(uppy: Uppy, opts: SearchPluginOptions) {
    super(uppy, opts);
    this.id = opts.id || 'FileSearchPlugin';
    this.type = 'acquirer';
    this.originalFiles = [];
    this.container = null;
    this.searchTimeout = null;
    this.mountingTimeouts = [];
    this.isMounting = false;
    this.mutationObserver = null;
    this.currentSearchQuery = '';

    // Bind event handlers
    this.onModalClosed = this.unmountSearchUI.bind(this);
    this.onFileAdded = this.addToOriginalFiles.bind(this);
    this.onFileRemoved = (file) => this.removeFromOriginalFiles(file.id);
  }

  // Render the search input UI
  render(): HTMLElement {
    if (this.container) {
      return this.container;
    }

    const container = document.createElement('div');
    container.className = 'uppy-SearchPlugin-container';
    container.id = `uppy-search-${this.uppy.getID()}`;

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'uppy-SearchPlugin-input';
    input.placeholder = 'Search files...';

    input.addEventListener('input', (event: Event) => {
      const target = event.target as HTMLInputElement;
      this.handleSearchWithDebounce(target.value);
    });

    container.appendChild(input);
    this.container = container;
    return container;
  }

  // Toggle search input enabled/disabled state
  private toggleSearchInputState(enabled: boolean): void {
    if (this.container) {
      const input = this.container.querySelector('input') as HTMLInputElement;
      if (input) {
        input.disabled = !enabled;
      }
    }
  }

  // Handle search input with debounce
  private handleSearchWithDebounce(query: string): void {
    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Set new timeout for debounced search
    this.searchTimeout = setTimeout(() => {
      this.handleSearch(query);
    }, 300); // 300ms debounce delay
  }

  // Handle search input and filter files
  private handleSearch(query: string): void {
    const dashboard = this.uppy.getPlugin('Dashboard') as any;
    if (!dashboard) {
      return;
    }

    // Store current search query for persistence
    this.currentSearchQuery = query;

    // Initialize original files if empty (first time)
    if (this.originalFiles.length === 0) {
      const currentFiles = Object.values(this.uppy.getState().files) as UppyFile[];
      this.originalFiles = [...currentFiles];
    } else if (this.hasAnyFileBeenEdited()) {
      // Replace edited files in originalFiles with current versions
      const currentFiles = this.uppy.getState().files;
      this.originalFiles = this.originalFiles.map(originalFile => {
        const currentFile = currentFiles[originalFile.id];
        if (currentFile && this.hasFileBeenEdited(originalFile, currentFile)) {
          return currentFile;
        }
        return originalFile;
      });
    }

    // Filter files based on query
    const filteredFiles = query
      ? this.originalFiles.filter((file) =>
        file.name.toLowerCase().includes(query.toLowerCase())
      )
      : this.originalFiles;

    // Update Uppy state with filtered files
    if (filteredFiles.length > 0 || query === '') {
      const updatedFiles: { [key: string]: UppyFile } = {};
      filteredFiles.forEach((file) => {
        updatedFiles[file.id] = file;
      });

      this.uppy.setState({
        files: updatedFiles,
      });
    }

    // Hide/show .uppy-Dashboard-files based on search results
    this.toggleDashboardFilesVisibility(filteredFiles.length, query.length);

    // Setup mutation observer to handle Uppy re-renders
    this.setupMutationObserver();

    // Force Dashboard to re-render
    dashboard.render(this.uppy.getState());
  }

  // Setup mutation observer to handle Uppy re-renders
  private setupMutationObserver(): void {
    // Disconnect existing observer
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    const dashboard = this.uppy.getPlugin('Dashboard') as any;
    if (!dashboard?.el) return;

    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldReapply = false;

      mutations.forEach((mutation) => {
        // Check if dashboard files were added/removed/modified
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          const target = mutation.target as HTMLElement;
          if (target.classList?.contains('uppy-Dashboard-files') ||
            target.querySelector?.('.uppy-Dashboard-files')) {
            shouldReapply = true;
          }
        }
      });

      if (shouldReapply && this.currentSearchQuery) {
        // Reapply visibility classes after Uppy re-renders
        setTimeout(() => {
          this.reapplyVisibilityClasses();
        }, 0);
      }
    });

    // Start observing the dashboard element
    this.mutationObserver.observe(dashboard.el, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });
  }

  // Common function to show or hide file items and action buttons
  private setFileItemsAndActionButtonsVisibility(
    dashboardFilesElement: Element,
    fileItems: NodeListOf<Element>,
    shouldHide: boolean,
    currentFileNames?: string[]
  ): void {
    if (currentFileNames) {
      // Complex filtering: show only items that match the search
      fileItems.forEach(item => {
        const itemNameElement = item.getElementsByClassName('uppy-Dashboard-Item-name')[0];
        if (itemNameElement) {
          const itemName = itemNameElement.getAttribute('title')?.toLowerCase() || '';
          const shouldShow = currentFileNames.includes(itemName);

          if (shouldShow) {
            (item as HTMLElement).classList.remove('search-hidden');
          } else {
            (item as HTMLElement).classList.add('search-hidden');
          }
        }
      });

      // Show action buttons when files are visible
      const actionButtons = dashboardFilesElement.querySelectorAll('.uppy-Dashboard-Item-action--remove, .uppy-Dashboard-Item-action--edit');
      actionButtons.forEach(button => {
        (button as HTMLElement).classList.remove('search-hidden');
      });
    } else {
      // Simple show/hide: toggle all items based on shouldHide parameter
      fileItems.forEach(item => {
        (item as HTMLElement).classList.toggle('search-hidden', shouldHide);
      });

      // Handle action buttons visibility
      const actionButtons = dashboardFilesElement.querySelectorAll('.uppy-Dashboard-Item-action--remove, .uppy-Dashboard-Item-action--edit');
      actionButtons.forEach(button => {
        (button as HTMLElement).classList.toggle('search-hidden', shouldHide);
      });
    }
  }

  // Common function to handle the entire visibility logic
  private handleFileVisibility(
    filteredFilesCount: number,
    queryLength: number,
    currentFileNames?: string[]
  ): void {
    const dashboard = this.uppy.getPlugin('Dashboard') as any;
    if (!dashboard || !dashboard.el) {
      return;
    }

    const dashboardFilesElement = dashboard.el.querySelector('.uppy-Dashboard-files');
    if (dashboardFilesElement) {
      // Find all file items (excluding the search container)
      const fileItems = dashboardFilesElement.querySelectorAll('.uppy-Dashboard-Item:not(.uppy-SearchPlugin-container)');

      // Find the first div with role="presentation" inside dashboardFilesElement
      const presentationDiv = dashboardFilesElement.querySelector('div[role="presentation"]');

      if (filteredFilesCount === 0 && queryLength > 0) {
        // Hide individual file items when no results found
        this.setFileItemsAndActionButtonsVisibility(dashboardFilesElement, fileItems, true);

        // Hide the presentation div that contains all file items
        if (presentationDiv) {
          (presentationDiv as HTMLElement).style.display = 'none';
        }
      } else if (filteredFilesCount > 0 && queryLength > 0) {
        // Show file items when results found - only show items that match the search
        this.setFileItemsAndActionButtonsVisibility(dashboardFilesElement, fileItems, false, currentFileNames);

        // Show the presentation div
        if (presentationDiv) {
          (presentationDiv as HTMLElement).style.display = '';
        }
      } else {
        // Show file items when results found or no query
        this.setFileItemsAndActionButtonsVisibility(dashboardFilesElement, fileItems, false);

        // Show the presentation div
        if (presentationDiv) {
          (presentationDiv as HTMLElement).style.display = '';
        }
      }
    }
  }

  // Reapply visibility classes after Uppy re-renders
  private reapplyVisibilityClasses(): void {
    if (!this.currentSearchQuery) return;

    const filteredFiles = this.originalFiles.filter((file) =>
      file.name.toLowerCase().includes(this.currentSearchQuery.toLowerCase())
    );

    // Get current file names for complex filtering
    const currentFiles = Object.values(this.uppy.getState().files);
    const currentFileNames = currentFiles.map(file => file.name.toLowerCase());

    // Use common function to handle visibility
    this.handleFileVisibility(filteredFiles.length, this.currentSearchQuery.length, currentFileNames);

    // Update header file count to show filtered results
    this.updateHeaderFileCount(filteredFiles.length, this.currentSearchQuery.length);
  }

  // Toggle visibility of .uppy-Dashboard-files based on search results
  private toggleDashboardFilesVisibility(filteredFilesCount: number, queryLength: number): void {
    // Get current file names for complex filtering
    const currentFiles = Object.values(this.uppy.getState().files);
    const currentFileNames = currentFiles.map(file => file.name.toLowerCase());

    // Use common function to handle visibility
    this.handleFileVisibility(filteredFilesCount, queryLength, currentFileNames);

    // Update header file count to show filtered results
    this.updateHeaderFileCount(filteredFilesCount, queryLength);

    // Control upload button text and state - always show total original files count
    this.updateUploadButtonState(this.originalFiles.length, queryLength);
  }

  // Update header file count to show filtered results
  private updateHeaderFileCount(filteredFilesCount: number, queryLength: number): void {
    const dashboard = this.uppy.getPlugin('Dashboard') as any;
    if (!dashboard || !dashboard.el) {
      return;
    }

    // Find the header file count element (this might be in different places depending on Uppy version)
    const headerFileCount = dashboard.el.querySelector('.uppy-DashboardContent-title');

    if (headerFileCount) {
      if (queryLength > 0) {
        // Show filtered count when searching
        headerFileCount.textContent = `${filteredFilesCount} file${filteredFilesCount !== 1 ? 's' : ''} selected`;
      } else {
        // Show total count when not searching
        headerFileCount.textContent = `${this.originalFiles.length} file${this.originalFiles.length !== 1 ? 's' : ''} selected`;
      }
    }
  }

  // Update upload button text and disabled state based on search results
  private updateUploadButtonState(totalFilesCount: number, queryLength: number): void {
    const dashboard = this.uppy.getPlugin('Dashboard') as any;
    if (!dashboard || !dashboard.el) {
      return;
    }

    // Find the upload button - it could be in different places depending on Uppy version
    const uploadButton = dashboard.el.querySelector('.uppy-StatusBar-actionBtn--upload')

    if (uploadButton) {
      const button = uploadButton as HTMLButtonElement;

      // Always show total file count and enable upload regardless of search filter
      const fileCount = totalFilesCount;
      button.textContent = `Upload ${fileCount} file${fileCount !== 1 ? 's' : ''}`;
    }
  }

  // Install the plugin
  install(): void {
    const dashboard = this.uppy.getPlugin('Dashboard') as any;
    if (!dashboard) {
      console.error('SearchPlugin requires Dashboard plugin');
      return;
    }

    // Listen to file events to keep original files updated and mount search UI
    this.uppy.on('file-added', (file) => {
      this.addToOriginalFiles(file);
      this.mountSearchUI();
    });
    this.uppy.on('file-removed', (file) => this.removeFromOriginalFiles(file.id));

    // Listen for bulk file additions (like "Add more" button)
    this.uppy.on('files-added', () => {
      // Clear search and show all files when new files are added
      this.clearSearchAndShowAllFiles();
    });

    // Listen for upload state changes to enable/disable search
    this.uppy.on('upload', () => {
      // Disable search when upload starts
      this.toggleSearchInputState(false);
    });

    // Use 'complete' event instead - this fires once after ALL files are done
    this.uppy.on('complete', () => {
      // Re-enable search when ALL uploads are complete (success, error, or cancelled)
      this.toggleSearchInputState(true);
    });

    // Unmount search UI when modal is closed
    this.uppy.on('dashboard:modal-closed', this.onModalClosed);

    // Override upload behavior to upload all original files
    this.overrideUploadBehavior();

    // Add listener for "Add more" button clicks
    this.addAddMoreButtonListener();

    // Try multiple times to mount the search UI as Dashboard might not be ready immediately
    const attemptMount = (attempt = 0) => {
      if (attempt > 5) {
        console.warn('Failed to mount search UI after multiple attempts for', this.uppy.getID());
        return;
      }

      const dashboard = this.uppy.getPlugin('Dashboard') as any;
      if (dashboard && dashboard.el) {
        this.mountSearchUI();
      } else {
        setTimeout(() => attemptMount(attempt + 1), 100 * (attempt + 1));
      }
    };

    // Start attempting to mount
    attemptMount();
  }

  // Override upload behavior to upload all original files
  private overrideUploadBehavior(): void {
    // Store the original upload method
    const originalUpload = this.uppy.upload.bind(this.uppy);

    // Override the upload method
    this.uppy.upload = () => {
      // Clear search filter first to show all files
      this.clearSearchAndShowAllFiles();

      // Get current files from Uppy state (may contain edited versions)
      const currentFiles = this.uppy.getState().files;

      // Temporarily restore all original files to state before upload
      if (this.originalFiles.length > 0) {
        const allFiles: { [key: string]: UppyFile } = {};
        this.originalFiles.forEach((file) => {
          // Check if this file has been edited (has different data than original)
          const currentFile = currentFiles[file.id];
          if (currentFile && this.hasFileBeenEdited(file, currentFile)) {
            // Preserve the edited version
            allFiles[file.id] = currentFile;
          } else {
            // Use original file
            allFiles[file.id] = file;
          }
        });

        // Update state with all files (preserving edits)
        this.uppy.setState({ files: allFiles });
      }

      // Call the original upload method
      return originalUpload();
    };
  }

  // Check if any file has been edited by comparing with current Uppy state
  private hasAnyFileBeenEdited(): boolean {
    if (this.originalFiles.length === 0) {
      return false;
    }

    const currentFiles = this.uppy.getState().files;

    return this.originalFiles.some(originalFile => {
      const currentFile = currentFiles[originalFile.id];
      return currentFile && this.hasFileBeenEdited(originalFile, currentFile);
    });
  }

  // Check if a file has been edited by comparing key properties
  private hasFileBeenEdited(originalFile: UppyFile, currentFile: UppyFile): boolean {
    // Check if the file data has changed (indicating editing)
    if (!currentFile || !originalFile) return false;

    return (
      currentFile.data !== originalFile.data ||
      currentFile.size !== originalFile.size ||
      currentFile.type !== originalFile.type ||
      // Check for image editor specific changes
      (currentFile.meta && currentFile.meta.imageEditor && currentFile.meta.imageEditor !== originalFile.meta?.imageEditor)
    );
  }

  // Add a file to the original files list
  private addToOriginalFiles(file: UppyFile): void {
    const existingFile = this.originalFiles.find(f => f.id === file.id);
    if (!existingFile) {
      this.originalFiles.push(file);

      // Mount search UI after first file is added
      this.mountSearchUI();

      // Update UI state after adding new files
      this.updateUIAfterFileChange();
    }
  }

  // Remove a file from the original files list
  private removeFromOriginalFiles(fileId: string): void {
    this.originalFiles = this.originalFiles.filter(f => f.id !== fileId);

    // Update UI state after removing files
    this.updateUIAfterFileChange();
  }

  // Update UI state after file changes (add/remove)
  private updateUIAfterFileChange(): void {

    // Update header file count
    this.updateHeaderFileCount(this.originalFiles.length, 0);

    // Update upload button
    this.updateUploadButtonState(this.originalFiles.length, 0);

    // If there's an active search, reapply the search filter
    if (this.currentSearchQuery) {
      this.handleSearch(this.currentSearchQuery);
    }
  }


  // Clear search query and show all files
  private clearSearchAndShowAllFiles(): void {
    // Clear the search input
    if (this.container) {
      const input = this.container.querySelector('input') as HTMLInputElement;
      if (input) {
        input.value = '';
      }
    }

    // Clear search query
    this.currentSearchQuery = '';


    if (this.hasAnyFileBeenEdited()) {
      // Replace edited files in originalFiles with current versions
      const currentFiles = this.uppy.getState().files;
      this.originalFiles = this.originalFiles.map(originalFile => {
        const currentFile = currentFiles[originalFile.id];
        if (currentFile && this.hasFileBeenEdited(originalFile, currentFile)) {
          return currentFile;
        }
        return originalFile;
      });
    }

    // Restore all files to Uppy state
    const updatedFiles: { [key: string]: UppyFile } = {};
    this.originalFiles.forEach((file) => {
      updatedFiles[file.id] = file;
    });

    this.uppy.setState({
      files: updatedFiles,
    });

    // Update UI elements
    this.updateHeaderFileCount(this.originalFiles.length, 0);
    this.updateUploadButtonState(this.originalFiles.length, 0);

    // Show all file items
    this.toggleDashboardFilesVisibility(this.originalFiles.length, 0);
  }

  // Add listener for "Add more" button clicks
  private addAddMoreButtonListener(): void {
    // Use a MutationObserver to detect when the "Add more" button appears
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Look for "Add more" button in the added nodes
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              // Check if this is the "Add more" button or contains it
              const addMoreButton = element.querySelector?.('.uppy-Dashboard-Item--AddMore') ||
                (element.classList?.contains('uppy-Dashboard-Item--AddMore') ? element : null);

              if (addMoreButton) {
                addMoreButton.addEventListener('click', () => {
                  // Clear search and show all files when "Add more" is clicked
                  this.uppy.setState({
                    files: this.originalFiles,
                  });
                  this.clearSearchAndShowAllFiles();
                });
              }
            }
          });
        }
      });
    });

    // Start observing the dashboard for changes
    const dashboard = this.uppy.getPlugin('Dashboard') as any;
    if (dashboard?.el) {
      observer.observe(dashboard.el, {
        childList: true,
        subtree: true
      });
    }

    // Store observer for cleanup
    (this as any).addMoreObserver = observer;
  }

  // Mount the search UI into the Dashboard
  private mountSearchUI(): void {
    // Prevent multiple simultaneous mounting attempts
    if (this.isMounting) {
      return;
    }

    // Get the Dashboard plugin instance for THIS specific Uppy instance
    const dashboard = this.uppy.getPlugin('Dashboard') as any;
    if (!dashboard?.el) {
      return;
    }

    // Check if already mounted to prevent duplicate mounting
    if (this.container && this.container.parentNode) {
      return;
    }

    this.isMounting = true;

    const MAX_ATTEMPTS = 10;
    const RETRY_DELAY = 50; // base delay in ms

    const tryMount = (attempt = 0): void => {
      const dashboardFiles = dashboard.el!.querySelector('.uppy-Dashboard-files');

      if (dashboardFiles) {
        // Cleanup: cancel pending retries
        this.mountingTimeouts.forEach(clearTimeout);
        this.mountingTimeouts = [];

        const searchContainer = this.render();

        // Insert at the top of the dashboard files container
        dashboardFiles.insertBefore(searchContainer, dashboardFiles.firstChild);

        // Add a unique identifier to help with debugging
        searchContainer.setAttribute('data-uppy-instance', this.uppy.getID());
        this.isMounting = false;
        return;
      }

      if (attempt >= MAX_ATTEMPTS) {
        console.warn(
          `[Uppy SearchUI] Failed to find Dashboard files element after ${MAX_ATTEMPTS} attempts for ${this.uppy.getID()}`
        );
        this.isMounting = false;
        return;
      }

      const delay = RETRY_DELAY * (attempt + 1);
      const timeoutId = setTimeout(() => tryMount(attempt + 1), delay);
      this.mountingTimeouts.push(timeoutId);
    };

    tryMount();
  }

  // Unmount the search UI
  private unmountSearchUI(): void {
    // Clear any pending mounting timeouts
    this.mountingTimeouts.forEach(timeout => clearTimeout(timeout));
    this.mountingTimeouts = [];
    this.isMounting = false;

    // Disconnect mutation observer
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    // Disconnect add more button observer
    if ((this as any).addMoreObserver) {
      (this as any).addMoreObserver.disconnect();
      (this as any).addMoreObserver = null;
    }

    // Clear search query
    this.currentSearchQuery = '';

    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }

  // Cleanup on uninstall
  uninstall(): void {
    // Clear any pending search timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }

    // Clear any pending mounting timeouts
    this.mountingTimeouts.forEach(timeout => clearTimeout(timeout));
    this.mountingTimeouts = [];
    this.isMounting = false;

    // Restore original files
    if (this.originalFiles.length > 0) {
      const restoredFiles: { [key: string]: UppyFile } = {};
      this.originalFiles.forEach((file) => {
        restoredFiles[file.id] = file;
      });
      this.uppy.setState({ files: restoredFiles });
    }

    // Remove the search UI
    this.unmountSearchUI();

    // Remove all event listeners
    this.uppy.off('file-added', (file) => { this.addToOriginalFiles(file); this.mountSearchUI(); });
    this.uppy.off('dashboard:modal-closed', this.onModalClosed);
    this.uppy.off('file-removed', this.onFileRemoved);
    this.uppy.off('dashboard:file-card:show' as any, () => {});
  }
}
